import { type FormEvent, useEffect, useState } from "react";

import {
  type CreatedEngagement,
  type CreateFromTemplateRequest,
  type EnvironmentRole,
  PROVIDER_ENDPOINTS,
  type TemplateApplication,
  type TemplateDetail,
  type TemplateSummary,
  boundValue,
  createFromTemplate,
  getTemplate,
  listApplications,
  listTemplates,
  problemsOf,
} from "./templatesClient";

function List({ items }: { items: string[] }) {
  return (
    <ul>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function TemplateSections({ detail }: { detail: TemplateDetail }) {
  const t = detail.template;
  return (
    <div className="template-sections">
      <section aria-label="Intended use">
        <h3>Intended use</h3>
        <p>
          <strong>{t.intended_use.title}</strong> ({t.intended_use.use_class})
        </p>
        <p>{t.intended_use.statement}</p>
        <h4>Permitted uses</h4>
        <List items={t.intended_use.permitted_uses} />
        <h4>Prohibited uses</h4>
        <List items={t.intended_use.prohibited_uses} />
      </section>
      <section aria-label="Destination contract">
        <h3>Destination contract and target domains</h3>
        <p>
          {t.destination.name}; binds to {t.destination.allowed_environment_roles.join(", ")}{" "}
          environments
        </p>
        <p>Target domains: {t.destination.target_domains.join(", ")}</p>
        <p>Target objects: {t.destination.target_objects.join(", ")}</p>
      </section>
      <section aria-label="Source population">
        <h3>Source population</h3>
        <p>{t.source_population.statement}</p>
        <p>Trust: {t.trust.required_trust}. {t.trust.consequence_of_error}</p>
      </section>
      <section aria-label="Review roles">
        <h3>Review roles</h3>
        <List
          items={t.review_roles.map(
            (role) =>
              `${role.responsibility} (${role.role_code})${role.required ? "" : ", optional"}: ${role.description}`,
          )}
        />
      </section>
      <section aria-label="Provider policy">
        <h3>Provider policy</h3>
        {t.provider_policy === null ? (
          <p>No AI provider: deterministic processing only.</p>
        ) : (
          <p>
            Fallback {t.provider_policy.fallback.allowed ? "allowed" : "off"}; engagement budget{" "}
            {String(t.provider_policy.budgets.engagement_budget_usd ?? "unlimited")} USD. The
            customer&apos;s approved provider is chosen below.
          </p>
        )}
      </section>
      <section aria-label="Output policy">
        <h3>Output policy</h3>
        <p>
          Release: {t.output_policy.release_mode}; outputs: {t.output_policy.formats.join(", ")}
        </p>
      </section>
      <section aria-label="Verification plan">
        <h3>Verification plan</h3>
        <p>{t.verification_plan.summary}</p>
        <ol>
          {t.verification_plan.steps.map((step) => (
            <li key={step.name}>
              {step.name}: {step.method}
            </li>
          ))}
        </ol>
        <p>Acceptance: {t.verification_plan.acceptance}</p>
      </section>
    </div>
  );
}

function CreateForm({
  detail,
  onCreated,
}: {
  detail: TemplateDetail;
  onCreated: (created: CreatedEngagement) => void;
}) {
  const t = detail.template;
  const roles = t.destination.allowed_environment_roles;
  const [declare, setDeclare] = useState(true);
  const [provider, setProvider] = useState("anthropic");
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    const profileKey = text("profile_key");
    const request: CreateFromTemplateRequest = {
      reason: text("reason"),
      engagement_key: text("engagement_key"),
      name: text("name"),
      environment: declare
        ? {
            profile_key: profileKey,
            environment_name: text("environment_name"),
            environment_role: text("environment_role") as EnvironmentRole,
          }
        : { profile_key: profileKey },
      parties: t.review_roles
        .map((role) => ({
          responsibility: role.responsibility,
          party_name: text(`party.${role.responsibility}`),
        }))
        .filter((party) => party.party_name.length > 0),
      source_bounds: Object.fromEntries(
        t.source_population.required_bounds.map((key) => [key, boundValue(text(`bound.${key}`))]),
      ),
    };
    if (t.provider_policy !== null) {
      request.provider = {
        grants: [
          {
            provider,
            models: [text("model")],
            endpoint: text("endpoint"),
            credential_ref: text("credential_ref"),
          },
        ],
        default_provider: provider,
      };
    }
    setBusy(true);
    setProblems([]);
    try {
      onCreated(await createFromTemplate(t.template_key, request));
    } catch (caught) {
      setProblems(problemsOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form aria-label="Create engagement" onSubmit={(event) => void onSubmit(event)}>
      <h3>Create the engagement</h3>
      <label>
        Engagement key
        <input name="engagement_key" required pattern="[a-z0-9][a-z0-9._\-]{1,62}" />
      </label>
      <label>
        Engagement name
        <input name="name" required />
      </label>
      <fieldset>
        <legend>Destination environment</legend>
        <label>
          <input type="checkbox" checked={declare} onChange={(e) => setDeclare(e.target.checked)} />
          Declare a new environment profile
        </label>
        <label>
          Profile key
          <input name="profile_key" required />
        </label>
        {declare && (
          <>
            <label>
              Environment name
              <input name="environment_name" required />
            </label>
            <label>
              Environment role
              <select name="environment_role" defaultValue={roles[0]}>
                {roles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
      </fieldset>
      <fieldset>
        <legend>Parties</legend>
        {t.review_roles.map((role) => (
          <label key={role.responsibility}>
            {role.responsibility}
            {role.required ? "" : " (optional)"}
            <input name={`party.${role.responsibility}`} required={role.required} />
          </label>
        ))}
      </fieldset>
      {t.source_population.required_bounds.length > 0 && (
        <fieldset>
          <legend>Source population bounds (comma-separated lists)</legend>
          {t.source_population.required_bounds.map((key) => (
            <label key={key}>
              {key}
              <input name={`bound.${key}`} required />
            </label>
          ))}
        </fieldset>
      )}
      {t.provider_policy !== null && (
        <fieldset>
          <legend>Approved AI provider</legend>
          <label>
            Provider
            <select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {Object.keys(PROVIDER_ENDPOINTS).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Model
            <input name="model" required />
          </label>
          <label>
            Endpoint
            <input name="endpoint" key={provider} defaultValue={PROVIDER_ENDPOINTS[provider]} required />
          </label>
          <label>
            Credential reference
            <input
              name="credential_ref"
              key={`${provider}-ref`}
              defaultValue={`secret://${provider}_api_key`}
              required
            />
          </label>
        </fieldset>
      )}
      <label>
        Reason
        <input name="reason" required />
      </label>
      {problems.length > 0 && (
        <div role="alert">
          <ul>
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}
      <button type="submit" disabled={busy}>
        Create engagement
      </button>
    </form>
  );
}

/** Engagement setup from a template (DESIGN-SPEC 28.2; task 4170.1): pick a common engagement
 * shape, review what it pre-populates, supply what only the customer can, and create the
 * engagement, its intended use, destination contract, scope, and provider policy in one step. */
export function EngagementTemplatesPage() {
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [created, setCreated] = useState<CreatedEngagement | null>(null);
  const [recent, setRecent] = useState<TemplateApplication[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listTemplates()
      .then(setTemplates)
      .catch((caught: unknown) => setError(problemsOf(caught).join("; ")));
    listApplications()
      .then(setRecent)
      .catch(() => setRecent([]));
  }, []);

  async function choose(key: string) {
    setCreated(null);
    setError(null);
    try {
      setDetail(await getTemplate(key));
    } catch (caught) {
      setError(problemsOf(caught).join("; "));
    }
  }

  function onCreated(result: CreatedEngagement) {
    setCreated(result);
    setRecent((items) => [result.application, ...items]);
  }

  return (
    <div className="engagement-templates">
      <h1>New engagement from a template</h1>
      <p>
        <a href="/engagements/review-capacity">Plan review capacity</a>
      </p>
      {error && <p role="alert">{error}</p>}
      <nav aria-label="Engagement templates">
        <ul>
          {templates.map((t) => (
            <li key={t.template_key}>
              <button type="button" onClick={() => void choose(t.template_key)}>
                {t.title}
              </button>{" "}
              <small>
                {t.origin} v{t.version} · {t.engagement_mode}
              </small>
            </li>
          ))}
        </ul>
      </nav>
      {detail && (
        <article aria-label={detail.template.title}>
          <h2>
            {detail.template.title} (v{detail.template.version})
          </h2>
          <p>{detail.template.summary}</p>
          <TemplateSections detail={detail} />
          {created ? (
            <p role="status">
              Created draft engagement {created.engagement.engagement_key} (scope version{" "}
              {created.scope.version}
              {created.application.provider_policy_version !== null
                ? `, provider policy version ${created.application.provider_policy_version}`
                : ""}
              ) from {created.application.template_key} v{created.application.template_version}.
            </p>
          ) : (
            <CreateForm detail={detail} onCreated={onCreated} />
          )}
        </article>
      )}
      {recent.length > 0 && (
        <section aria-label="Engagements created from templates">
          <h2>Engagements created from templates</h2>
          <table>
            <thead>
              <tr>
                <th>Engagement</th>
                <th>Template</th>
                <th>Created by</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((a) => (
                <tr key={a.id}>
                  <td>{a.engagement_key}</td>
                  <td>
                    {a.template_key} v{a.template_version}
                  </td>
                  <td>{a.applied_by}</td>
                  <td>{a.applied_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
